import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useAppSelector } from "@/redux/features/hooks";
import { useGetRestaurantTablesQuery } from "@/redux/services/table/table.api";
import { useState } from "react";
import { OrderType } from "@/redux/services/order/order.type";
import { ORDER_TYPE_LABELS } from "./types";

export interface NewOrderConfig {
  orderType: OrderType;
  tableId?: string;
}

interface NewOrderModalProps {
  open: boolean;
  onClose: () => void;
  onCreate: (config: NewOrderConfig) => void;
}

const NewOrderModal: React.FC<NewOrderModalProps> = ({
  open,
  onClose,
  onCreate,
}) => {
  const restaurant = useAppSelector((state) => state.user.currentRestaurant);
  const [orderType, setOrderType] = useState<OrderType>("dine_in");
  const [tableId, setTableId] = useState<string | undefined>();

  const { data: tablesData } = useGetRestaurantTablesQuery(
    { restaurantId: restaurant?.id },
    { skip: !restaurant?.id }
  );

  const tables = tablesData?.results ?? [];

  const canCreate =
    orderType === "dine_in" ? !!tableId : true;

  const handleCreate = () => {
    if (!canCreate) return;
    onCreate({
      orderType,
      tableId: orderType === "dine_in" ? tableId : undefined,
    });
    setOrderType("dine_in");
    setTableId(undefined);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Nouvelle commande</DialogTitle>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          {/* Order type selector */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Type de commande</label>
            <div className="grid grid-cols-3 gap-2">
              {(Object.entries(ORDER_TYPE_LABELS) as [OrderType, string][]).map(
                ([type, label]) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => {
                      setOrderType(type);
                      if (type !== "dine_in") setTableId(undefined);
                    }}
                    className={`rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors ${
                      orderType === type
                        ? "border-primary bg-primary text-white"
                        : "border-border bg-background text-foreground hover:bg-muted"
                    }`}
                  >
                    {label}
                  </button>
                )
              )}
            </div>
          </div>

          {/* Table selector — only for dine_in */}
          {orderType === "dine_in" && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Table</label>
              <Select
                value={tableId ?? ""}
                onValueChange={(value) => setTableId(value || undefined)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner une table" />
                </SelectTrigger>
                <SelectContent>
                  {tables.map((table) => (
                    <SelectItem key={table.id} value={table.id}>
                      Table {table.code}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <Button onClick={handleCreate} disabled={!canCreate} className="w-full" size="lg">
            Créer la commande
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NewOrderModal;
